"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Cpu, Flame, Gauge, Layers, PhoneCall, Settings, ShieldCheck, Thermometer, Wrench, Zap } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const componentsList = [{
  name: "Heating Elements & Thermostats",
  type: "Electric Units",
  description: "Upper and lower high-wattage copper/incoloy immersion elements, paired with dual bi-metal temperature controllers.",
  symptom: "No hot water or lukewarm recovery cycles.",
  action: "Resistance ohm-testing, voltage drop isolation, and direct OEM replacement."
}, {
  name: "Thermopile & Pilot Assemblies",
  type: "Gas Units",
  description: "Millivolt flame sensors, spark igniters, and safety shutoff thermocouple linkages.",
  symptom: "Pilot light will not stay lit; burner drops out.",
  action: "Microamp output testing, gas orifice clearing, and safety sensor replacement."
}, {
  name: "Temperature & Pressure (T&P) Relief Valve",
  type: "All Units",
  description: "ASME-rated mechanical pressure and temperature safety release valve (rated at 150 PSI / 210°F).",
  symptom: "Active dripping or discharge pipe leakage.",
  action: "Sediment flush, valve spring diagnostic, and threaded code-compliant replacement."
}, {
  name: "Sacrificial Anode Rods",
  type: "Tank Systems",
  description: "Magnesium or aluminum-zinc cathodic protection rods designed to prevent steel tank oxidation.",
  symptom: "Rotten egg sulfur odors, rusty water, tank corrosion.",
  action: "Core wear inspection, torque extraction, and powered or hex-head rod re-installation."
}, {
  name: "Gas Control Valve & Electronic Boards",
  type: "Gas & Smart Units",
  description: "Integrated electronic control valves, status LED diagnostic boards, and gas manifold actuators.",
  symptom: "Error blink codes, intermittent lockout, no gas ignition.",
  action: "Diagnostic code scanning, manifold pressure validation, and smart valve calibration."
}, {
  name: "Tankless Heat Exchangers & Flow Sensors",
  type: "Tankless",
  description: "Multi-pass copper heat exchangers, vortex water flow turbines, and modulation fans.",
  symptom: "Fluctuating water temperatures ('cold water sandwich') or error code lockouts.",
  action: "Chemical descaling flush, turbine debris extraction, and sensor calibration."
}];
const diagnosticSteps = [{
  step: "01",
  title: "Electrical & Pressure Safety Verification",
  description: "We test incoming line voltage, isolate breaker circuits, measure static municipal water PSI, and examine the expansion tank bladder health."
}, {
  step: "02",
  title: "Component-Level Diagnostic Testing",
  description: "Using calibrated multimeters and gas manometers, our technicians test resistance, millivolts, burner pressure, and thermal safety switches."
}, {
  step: "03",
  title: "Sediment & Tank Integrity Inspection",
  description: "We check lower tank calcification, dip tube integrity, and fittings for galvanic corrosion or hairline stress fractures."
}, {
  step: "04",
  title: "Factory-Spec Repair & Code Testing",
  description: "Damaged parts are replaced with OEM hardware, followed by a pressure test, thermal cycle verification, and safety inspection."
}];
const technicalFaqs = [{
  id: "item-1",
  question: "How do you determine if an element or thermostat failed?",
  answer: "We isolate the power and measure element resistance using a calibrated multimeter. A healthy 4500W element reads between 11.5 and 13.5 ohms. A reading of infinity indicates an open internal circuit (burned-out element), while a grounded reading indicates element sheath compromise."
}, {
  id: "item-2",
  question: "What causes pilot lights to repeatedly extinguish?",
  answer: "The most common cause is a degraded thermocouple or thermopile generating below the required 25-30 millivolts needed to hold the internal gas safety valve open. Other frequent causes include clogged pilot burner orifices, air intake screen blockages, or a faulty thermal cutoff switch."
}, {
  id: "item-3",
  question: "Why does my hot water smell metallic or like sulfur?",
  answer: "Anaerobic bacteria in water react with standard magnesium sacrificial anode rods to produce hydrogen sulfide gas. We resolve this by draining and disinfecting the tank, followed by replacing the rod with an aluminum-zinc or powered electronic titanium anode."
}, {
  id: "item-4",
  question: "When is repair no longer viable compared to replacement?",
  answer: "When internal glass tank lining fractures cause structural tank leaks, repairs are impossible because the inner steel vessel is compromised. However, issues with valves, thermostats, burners, sensors, and elements can be completely repaired same-day."
}];
export default function HeaterTechnical() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("all");
  const filteredComponents = activeTab === "all" ? componentsList : componentsList.filter(item => activeTab === "electric" ? item.type.includes("Electric") || item.type.includes("All") : activeTab === "gas" ? item.type.includes("Gas") || item.type.includes("All") : item.type.includes("Tankless"));
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="waterheaterrepairtechnical" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3">
            <Wrench className="size-3 text-primary mr-1" />
            Engineering & Diagnostics
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Water Heater Repair Methods & Serviced Components
          </h2>
          <p className="mt-3 text-base md:text-lg text-muted-foreground leading-relaxed">
            Our certified Jonesboro technicians use calibrated diagnostic tools to pinpoint component failure and restore hot water systems to exact manufacturer specifications.
          </p>
        </div>

        {/* Technical Diagnostics Workflow */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Activity className="size-5 text-primary" />
                Standard Diagnostic Protocol
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Every repair follows a structured four-stage evaluation before parts replacement.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {diagnosticSteps.map((step, i) => <div key={step.step} data-index={i} className="bg-card text-card-foreground p-5 rounded-xl border border-border shadow-xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-xs font-bold px-2 py-1 rounded-md bg-primary/10 text-primary">
                      PHASE {step.step}
                    </span>
                    <ShieldCheck className="size-4 text-muted-foreground" />
                  </div>
                  <h4 className="font-semibold text-foreground text-base mb-2">
                    {step.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-normal">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Components Serviced Filterable Grid */}
        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Settings className="size-5 text-primary" />
                Key Components Serviced & Replaced
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Full-service troubleshooting on standard tanks, high-efficiency gas, and tankless units.
              </p>
            </div>

            {/* System Type Filter Tabs */}
            <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="w-auto">
              <TabsList className="bg-muted">
                <TabsTrigger value="all">All Units</TabsTrigger>
                <TabsTrigger value="electric">Electric</TabsTrigger>
                <TabsTrigger value="gas">Gas</TabsTrigger>
                <TabsTrigger value="tankless">Tankless</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <motion.div key={activeTab} variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredComponents.map((comp, i) => <div key={comp.name} data-index={i}>
                <Card className="h-full border border-border shadow-xs hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">
                        {comp.type}
                      </Badge>
                      {comp.type.includes("Gas") ? <Flame className="size-4 text-accent" /> : comp.type.includes("Electric") ? <Zap className="size-4 text-primary" /> : <Cpu className="size-4 text-primary" />}
                    </div>
                    <CardTitle className="text-lg text-foreground font-semibold">
                      {comp.name}
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground">
                      {comp.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    <Separator />
                    <div className="space-y-2 text-xs">
                      <div>
                        <span className="font-semibold text-destructive inline-flex items-center gap-1">
                          <AlertCircle className="size-3" /> Failure Indicator:
                        </span>
                        <p className="text-muted-foreground mt-0.5">
                          {comp.symptom}
                        </p>
                      </div>
                      <div>
                        <span className="font-semibold text-primary inline-flex items-center gap-1">
                          <CheckCircle2 className="size-3" /> Technical Procedure:
                        </span>
                        <p className="text-muted-foreground mt-0.5">
                          {comp.action}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </motion.div>
        </div>

        {/* Technical FAQ & Fast Action Banner */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* FAQ Accordion */}
          <div className="lg:col-span-7">
            <h3 className="text-xl font-bold text-foreground mb-1 flex items-center gap-2">
              <Thermometer className="size-5 text-primary" />
              Technical Repair FAQ
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Direct technical explanations for common heating system failures in Jonesboro homes.
            </p>

            <Accordion type="single" collapsible className="w-full bg-card rounded-xl border border-border p-4 shadow-xs">
              {technicalFaqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i}>
                  <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline hover:text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Quick Support Card */}
          <div className="lg:col-span-5">
            <Card className="bg-secondary text-secondary-foreground border border-border">
              <CardHeader>
                <Badge variant="default" className="w-fit mb-1">
                  24/7 Rapid Response
                </Badge>
                <CardTitle className="text-xl text-foreground font-bold">
                  Need a Master Plumber Diagnostic?
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Our service vehicles arrive stocked with universal heating elements, standard thermocouples, OEM gas valves, and relief valves.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm text-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Same-day parts replacement and thermal restoration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Clayton County code & safety compliance verification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Clear, flat diagnostic pricing before any repair begins</span>
                  </div>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="w-full sm:w-auto">
                    <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call for Emergency Service</span>
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                    <Link to="/contact/">
                      <span>Schedule Diagnostic</span>
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}